// ═══════════════════════════════════════════════════════════════════════
// masterArchitectureTab.jsx — Master CLM architecture (with/without agentic)
// ═══════════════════════════════════════════════════════════════════════
// Two-perspective architecture viewer:
//   1. CLASSICAL CLM — 7 systems + bus + governance
//   2. AGENTIC CLM   — adds 27 agents + control planes (trust, policy, audit)
// ISA-95 layered (L0-L4 + external).
// ═══════════════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { AGENTS, HUMAN_GATES } from "./agenticAIData";
import { T, FONTS } from "./theme";

// Layered ISA-95 view of the CLM mesh
const LAYERS = [
  { id: "L4", name: "Level 4 · Enterprise",     color: T.gold,
    systems: [
      { id: "TC",   name: "Teamcenter PLM",   version: "2512", role: "Product lifecycle anchor" },
      { id: "SAP",  name: "SAP S/4HANA",      version: "2023+", role: "Financial system of record" },
      { id: "IX",   name: "Insights Hub",     version: "IOX",  role: "Cloud analytics / AI" },
      { id: "MDX",  name: "Mendix",           version: "10",   role: "Low-code / MDM front-end" },
    ] },
  { id: "L3", name: "Level 3 · MES",            color: T.blue,
    systems: [
      { id: "OPC",  name: "Opcenter EX",      version: "2507", role: "Manufacturing execution" },
      { id: "APS",  name: "Opcenter APS",     version: "2510", role: "Planning & scheduling" },
      { id: "AIG",  name: "AIG Gateway",      version: "2024", role: "TC ↔ everything events" },
      { id: "T4S",  name: "T4S Gateway",      version: "2024", role: "TC ↔ SAP bridge" },
    ] },
  { id: "L2", name: "Level 2 · Process Control", color: T.orange,
    systems: [
      { id: "SFC",  name: "SFC Terminals",    version: "2.0",  role: "Shop floor operator UI" },
      { id: "EDGE", name: "Industrial Edge",  version: "IEM 2.0", role: "OPC-UA aggregation, edge AI" },
      { id: "QUAL", name: "Quality Lab",      version: "1.0",  role: "Inspection / SPC / NCR" },
    ] },
  { id: "L1", name: "Level 1 · Sensors / Actuators", color: T.teal,
    systems: [
      { id: "PLC",  name: "PLCs",             version: "—",    role: "Programmable controllers" },
      { id: "CNC",  name: "CNC Machines",     version: "—",    role: "Computer numerical control" },
      { id: "ROB",  name: "Robotics",         version: "—",    role: "Industrial robots" },
      { id: "IOT",  name: "IIoT Sensors",     version: "—",    role: "Industrial IoT sensors" },
    ] },
  { id: "L0", name: "Level 0 · Physical Process", color: T.steel,
    systems: [
      { id: "CONV", name: "Material Handling",version: "—",    role: "Conveyors & flow" },
      { id: "PROC", name: "Production Process",version: "—",   role: "Physical production" },
    ] },
];

const PERSPECTIVES = [
  { id: "classical", label: "🏛️ Classical CLM",      color: T.cyan,
    desc: "7 Siemens systems + SAP + ESB · the foundation" },
  { id: "agentic",   label: "🤖 Agentic CLM",        color: T.green,
    desc: "Same foundation + 27 agents + 3 control planes + 12 inviolable gates" },
  { id: "compare",   label: "⇄ Side-by-side",         color: T.amber,
    desc: "Visual diff: what changes when you add agents" },
];

export default function MasterArchitectureTab() {
  const [view, setView] = useState("agentic");

  return (
    <div style={{ padding: "20px 24px", maxHeight: "calc(100vh - 108px)", overflowY: "auto" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: T.cyan, marginBottom: 4 }}>
          🗺️ CLM Master Architecture
        </div>
        <div style={{ fontSize: 11, color: T.text2 }}>
          Multi-perspective architecture · ISA-95 layered · 7 Siemens products + SAP + ESB ± 27 agents
        </div>
      </div>

      {/* Perspective toggle */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {PERSPECTIVES.map(p => (
          <button key={p.id} onClick={() => setView(p.id)}
            style={{
              flex: 1, padding: "12px 16px", borderRadius: 6,
              background: view === p.id ? `${p.color}15` : T.bg2,
              border: `1px solid ${view === p.id ? p.color : T.border}`,
              cursor: "pointer", textAlign: "left",
            }}>
            <div style={{ fontFamily: FONTS.header, fontSize: 12, fontWeight: 700,
                          color: view === p.id ? p.color : T.text }}>
              {p.label}
            </div>
            <div style={{ fontSize: 9, color: T.text2, marginTop: 4 }}>{p.desc}</div>
          </button>
        ))}
      </div>

      {view === "compare" ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <PerspectivePanel view="classical" />
          <PerspectivePanel view="agentic" />
        </div>
      ) : (
        <PerspectivePanel view={view} />
      )}

      {/* Diff summary when comparing */}
      {view === "compare" && (
        <div style={{
          marginTop: 18, padding: "14px 18px",
          background: T.bg2, border: `1px solid ${T.amber}30`, borderRadius: 8,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.amber, marginBottom: 10 }}>
            ⇄ What changes when you add agents
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10, fontSize: 9.5 }}>
            {[
              { adds: "Agents",     count: AGENTS.length,  what: "27 specialized agents across 7 zones", color: T.cyan },
              { adds: "Gates",      count: HUMAN_GATES.length, what: "12 inviolable human-control points", color: T.pink },
              { adds: "Control",    count: 3, what: "Trust + Identity + Audit planes", color: T.purple },
              { adds: "Governance", count: 3, what: "ARB · Ethics&Safety · Model Risk", color: T.gold },
              { adds: "Tools",      count: "~150", what: "Tool catalog scoped per agent", color: T.green },
              { adds: "KPIs",       count: 8, what: "4 agent + 4 program metrics", color: T.amber },
            ].map(d => (
              <div key={d.adds} style={{
                background: T.bg3, padding: "8px 10px", borderRadius: 4,
                borderLeft: `3px solid ${d.color}`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, color: d.color }}>+ {d.adds}</span>
                  <span style={{ fontFamily: FONTS.header, color: d.color, fontWeight: 700 }}>{d.count}</span>
                </div>
                <div style={{ color: T.text2 }}>{d.what}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// One perspective panel
function PerspectivePanel({ view }) {
  const isAgentic = view === "agentic";

  return (
    <div style={{
      background: T.bg2, border: `1px solid ${isAgentic ? T.green + "40" : T.cyan + "40"}`,
      borderRadius: 8, padding: "16px 18px",
    }}>
      <div style={{ fontFamily: FONTS.header, fontSize: 13, fontWeight: 700,
                    color: isAgentic ? T.green : T.cyan, marginBottom: 10, letterSpacing: 1 }}>
        {isAgentic ? "🤖 AGENTIC CLM" : "🏛️ CLASSICAL CLM"}
      </div>

      {/* If agentic: control plane on top */}
      {isAgentic && (
        <ControlPlaneBlock />
      )}

      {/* ISA-95 layers */}
      {LAYERS.map(layer => (
        <LayerBlock key={layer.id} layer={layer} isAgentic={isAgentic} />
      ))}

      {/* Common bus */}
      <BusBlock />

      {/* If agentic: governance/gates at bottom */}
      {isAgentic && (
        <div style={{
          marginTop: 10, padding: "12px 14px",
          background: `${T.pink}08`, border: `1px solid ${T.pink}30`, borderRadius: 6,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.pink, letterSpacing: 1, marginBottom: 4 }}>
            🛡️ {HUMAN_GATES.length} INVIOLABLE HUMAN-CONTROL GATES
          </div>
          <div style={{ fontSize: 9, color: T.text2 }}>
            Hard-coded in policy. No agent autonomy level — including L5 — overrides these.
          </div>
        </div>
      )}
    </div>
  );
}

function ControlPlaneBlock() {
  return (
    <div style={{
      marginBottom: 10, padding: "10px 14px",
      background: `${T.purple}08`, border: `1px solid ${T.purple}40`,
      borderTop: `3px solid ${T.purple}`, borderRadius: 6,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontFamily: FONTS.header, fontSize: 10, fontWeight: 700, color: T.purple, letterSpacing: 1 }}>
          AGENT CONTROL PLANE
        </span>
        <span style={{ fontSize: 8, color: T.purple, fontStyle: "italic" }}>new for agentic</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
        {[
          { name: "Identity & IAM",   detail: "SPIFFE/SPIRE · per-agent identity" },
          { name: "Policy & Authz",   detail: "OPA bundles · tool catalog · gates" },
          { name: "Audit Ledger",     detail: "Every action · query · output · signed" },
        ].map(c => (
          <div key={c.name} style={{ background: T.bg3, padding: "6px 8px", borderRadius: 4 }}>
            <div style={{ fontSize: 9, fontWeight: 600, color: T.purple }}>{c.name}</div>
            <div style={{ fontSize: 8, color: T.text2, marginTop: 2 }}>{c.detail}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LayerBlock({ layer, isAgentic }) {
  // Map system IDs to their agent zones
  const zoneMap = {
    "TC": "PLM", "AIG": "PLM", "T4S": "PLM",
    "SAP": "ERP",
    "OPC": "MES", "APS": "MES",
    "SFC": "SFC", "EDGE": "SFC", "PLC": "SFC", "CNC": "SFC", "ROB": "SFC", "IOT": "SFC",
    "QUAL": "QUAL",
    "IX": "CLOUD", "MDX": "CLOUD",
  };

  // Count agents on this layer
  const agentsHere = AGENTS.filter(a => {
    const sysIds = layer.systems.map(s => s.id);
    const zones = [...new Set(sysIds.map(id => zoneMap[id]).filter(Boolean))];
    return zones.includes(a.zone);
  });

  return (
    <div style={{
      marginBottom: 8, padding: "8px 12px",
      background: `${layer.color}08`, border: `1px solid ${layer.color}40`,
      borderLeft: `3px solid ${layer.color}`, borderRadius: 4,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontFamily: FONTS.header, fontSize: 9, color: layer.color, fontWeight: 700, letterSpacing: 1 }}>
          {layer.name}
        </span>
        {isAgentic && agentsHere.length > 0 && (
          <span style={{
            fontSize: 7, padding: "1px 6px", borderRadius: 8,
            background: `${T.green}15`, color: T.green, fontWeight: 600,
          }}>
            🤖 {agentsHere.length} agent{agentsHere.length > 1 ? "s" : ""}
          </span>
        )}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {layer.systems.map(sys => (
          <div key={sys.id} style={{
            padding: "4px 8px", background: T.bg3, borderRadius: 4,
            border: `1px solid ${layer.color}30`, fontSize: 8,
            minWidth: 80,
          }}>
            <div style={{ fontWeight: 700, color: layer.color }}>{sys.name}</div>
            <div style={{ color: T.text3, marginTop: 2 }}>{sys.version}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BusBlock() {
  return (
    <div style={{
      marginTop: 10, padding: "10px 14px",
      background: `${T.teal}10`, border: `1px solid ${T.teal}50`,
      borderTop: `3px solid ${T.teal}`, borderRadius: 6, textAlign: "center",
    }}>
      <div style={{ fontFamily: FONTS.header, fontSize: 12, fontWeight: 700, color: T.teal, letterSpacing: 2 }}>
        ⚡ KAFKA ESB · multi-AZ · schema-registered · routed · transformed · DLQ-backed
      </div>
      <div style={{ fontSize: 8, color: T.text2, marginTop: 4 }}>
        DURABLE · OBSERVABLE · RECOVERABLE · 99.95% tier-1 SLA · &lt; 500ms P99
      </div>
    </div>
  );
}
